import { TypeCurveForecast, PdpDeclineForecast } from 'types/api-responses';
import {
  ForecastDetails,
  ForecastStatusTag,
  InUseTag,
  type VolumeForecastType,
} from 'components/volume-source-picker';
import styles from './edit-volume-source-modal.module.scss';

interface ForecastListProps {
  /** Array of forecasts to display */
  forecasts: (TypeCurveForecast | PdpDeclineForecast)[];
  /** Currently selected forecast ID */
  selectedId: string | null;
  /** Callback when a forecast is selected */
  onSelect: (id: string) => void;
  /** Type of forecasts being displayed */
  forecastType: VolumeForecastType;
}

/**
 * Displays a list of forecast items with radio button selection.
 * @return {JSX.Element} The forecast list component
 */
export const ForecastList = ({
  forecasts,
  selectedId,
  onSelect,
  forecastType,
}: ForecastListProps) => {
  if (forecasts.length === 0) {
    return (
      <div className={styles.emptyState}>
        <p>
          No {forecastType === 'pdp_decline' ? 'PDP decline' : 'type curve'}{' '}
          forecasts available for this slot.
        </p>
      </div>
    );
  }

  return (
    <div className={styles.forecastList}>
      {forecasts.map((forecast) => {
        const isSelected = selectedId === String(forecast.id);

        return (
          <div
            key={forecast.id}
            className={`${styles.forecastItem} ${
              isSelected ? styles.selected : ''
            }`}
            onClick={() => onSelect(String(forecast.id))}
            role="button"
            tabIndex={0}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                onSelect(String(forecast.id));
              }
            }}
          >
            <div className={styles.forecastContent}>
              <div className={styles.forecastHeader}>
                <span className={styles.forecastName}>{forecast.name}</span>
                <ForecastStatusTag
                  forecast={forecast}
                  forecastType={forecastType}
                />
                {/* A type curve's `isCurrent` is the slot's priced source; a decline's is
                    only its well's latest, which the details line says. */}
                {forecastType === 'type_curve' && forecast.isCurrent && (
                  <InUseTag />
                )}
              </div>
              <div className={styles.forecastDetails}>
                <ForecastDetails
                  forecast={forecast}
                  forecastType={forecastType}
                />
              </div>
            </div>
            <div className={styles.radioContainer}>
              <input
                type="radio"
                name="forecast-selection"
                checked={isSelected}
                onChange={() => onSelect(String(forecast.id))}
                className={styles.radioInput}
                aria-label={`Select ${forecast.name}`}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};
